import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { AlertCircle, ArrowRight, BookOpen, Check, GraduationCap } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLocation } from '@/contexts/LocationContext';
import { supabase } from '@/integrations/supabase/client';
import { blogPosts, getPostUrl } from '@/data/blogPosts';

const STATE_OPTIONS = [
  { code: 'NSW', name: 'New South Wales' },
  { code: 'VIC', name: 'Victoria' },
  { code: 'WA', name: 'Western Australia' },
];

const YEAR_LEVELS = ['Year 3', 'Year 5', 'Year 7'];

const TENURE_OPTIONS = [
  { value: 'lt3', label: 'Less than 3 months' },
  { value: '3-6', label: '3 to 6 months' },
  { value: '6-12', label: '6 to 12 months' },
  { value: '1plus', label: '1 year or more' },
  { value: 'past', label: 'No longer attends' },
  { value: 'never', label: 'Never attended Schoolola' },
];

const BASE_PROGRAMS = [
  { value: 'english', label: 'English' },
  { value: 'mathematics', label: 'Mathematics' },
  { value: 'naplan', label: 'NAPLAN preparation' },
  { value: 'homework', label: 'Homework support' },
  { value: 'foundations', label: 'Foundations' },
];

const STATE_PROGRAMS: Record<string, { value: string; label: string }[]> = {
  NSW: [
    { value: 'oc', label: 'OC preparation' },
    { value: 'selective_nsw', label: 'NSW Selective school preparation' },
  ],
  VIC: [
    { value: 'vsec', label: 'VIC Selective entry (VSEC)' },
    { value: 'scholarship_vic', label: 'VIC Scholarship exam' },
  ],
  WA: [{ value: 'aset_gate', label: 'ASET / GATE preparation' }],
};

const RATING_QUESTIONS = [
  { key: 'rating_confidence', label: 'My child is more confident about school work' },
  { key: 'rating_academic_skills', label: 'My child has improved their academic skills' },
  { key: 'rating_supportive_teachers', label: 'The teachers are supportive and approachable' },
  { key: 'rating_naplan_practice', label: 'The NAPLAN practice has been useful' },
  { key: 'rating_overall', label: 'Overall, I am satisfied with Schoolola' },
] as const;

const BAND_QUESTIONS = [
  { key: 'naplan_reading', label: 'Reading' },
  { key: 'naplan_writing', label: 'Writing' },
  { key: 'naplan_numeracy', label: 'Numeracy' },
  { key: 'naplan_spelling', label: 'Spelling' },
  { key: 'naplan_grammar', label: 'Grammar and punctuation' },
] as const;

const BAND_OPTIONS = [
  { value: 'exceeding', label: 'Exceeding' },
  { value: 'strong', label: 'Strong' },
  { value: 'developing', label: 'Developing' },
  { value: 'support', label: 'Needs additional support' },
  { value: 'unknown', label: 'Prefer not to say' },
];

const RECOMMEND_OPTIONS = [
  { value: 'definitely', label: 'Definitely' },
  { value: 'probably', label: 'Probably' },
  { value: 'maybe', label: 'Maybe' },
  { value: 'no', label: 'No' },
];

const CONSENT_WORDING =
  "I consent to Schoolola using my child's first name, year level and my written feedback on our website and marketing material. This is optional and you can withdraw consent at any time by emailing hello@schoolola.com.au.";

const PROGRAM_LINKS: Record<string, { label: string; to: string }[]> = {
  NSW: [
    { label: 'OC preparation', to: '/programs/oc-preparation' },
    { label: 'NSW Selective school preparation', to: '/programs/selective-school' },
  ],
  VIC: [
    { label: 'VIC Selective entry (VSEC)', to: '/programs/vsec' },
    { label: 'VIC Scholarship exam', to: '/programs/scholarship-exam-course' },
  ],
  WA: [{ label: 'ASET / GATE preparation', to: '/courses/aset-gate-test-preparation' }],
};

const BLOG_TAGS: Record<string, string[]> = {
  NSW: [
    'NAPLAN',
    'NSW OC Test Preparation',
    'NSW Selective Preparation',
    'OC Placement Test',
    'OC Test',
    'Opportunity Class',
    'Selective High School',
    'Selective Schools',
    'Selective Test',
  ],
  VIC: ['NAPLAN', 'Victorian Scholarship', 'Scholarship', 'Selective', 'VIC Education'],
  WA: ['NAPLAN'],
};

const inputClass =
  'w-full box-border h-[52px] rounded-xl border-[1.5px] border-input bg-muted/60 px-4 text-sm text-foreground font-normal transition-colors focus:outline-none focus:border-primary focus:bg-background focus-visible:ring-2 focus-visible:ring-primary/30';
const textareaClass =
  'w-full box-border rounded-xl border-[1.5px] border-input bg-muted/60 px-4 py-3 text-sm text-foreground font-normal transition-colors focus:outline-none focus:border-primary focus:bg-background focus-visible:ring-2 focus-visible:ring-primary/30';
const labelClass = 'block text-sm font-medium text-foreground mb-2';
const legendClass = 'block text-sm font-medium text-foreground mb-3';
const headingClass = 'font-heading font-semibold tracking-[-0.015em] text-foreground';

/* Option card used for single and multi choice questions */
const OptionCard: React.FC<{
  label: string;
  selected: boolean;
  multi?: boolean;
  onSelect: () => void;
}> = ({ label, selected, multi, onSelect }) => (
  <button
    type="button"
    role={multi ? 'checkbox' : 'radio'}
    aria-checked={selected}
    onClick={onSelect}
    className={`flex min-w-0 items-center gap-3 rounded-[14px] border-[1.5px] p-4 text-left text-sm font-normal transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
      selected ? 'border-primary bg-primary/5 text-foreground' : 'border-input bg-background text-foreground hover:border-primary'
    }`}
  >
    <span
      aria-hidden="true"
      className={`flex h-[20px] w-[20px] shrink-0 items-center justify-center border-[1.5px] ${
        multi ? 'rounded-md' : 'rounded-full'
      } ${selected ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-background'}`}
    >
      {selected && (multi ? <Check className="h-3 w-3" /> : <span className="h-2 w-2 rounded-full bg-primary-foreground" />)}
    </span>
    <span className="min-w-0">{label}</span>
  </button>
);

const NaplanFeedback: React.FC = () => {
  const { selectedState, stateConfigs } = useLocation();
  const loadedAt = useRef<number>(Date.now());

  const initialState = STATE_OPTIONS.some((s) => s.code === selectedState) ? selectedState : 'NSW';

  const [stateCode, setStateCode] = useState<string>(initialState);
  const [parentFirstName, setParentFirstName] = useState('');
  const [parentLastName, setParentLastName] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [childName, setChildName] = useState('');
  const [yearLevel, setYearLevel] = useState('');
  const [centreSlug, setCentreSlug] = useState('');
  const [tenure, setTenure] = useState('');
  const [programs, setPrograms] = useState<string[]>([]);
  const [ratings, setRatings] = useState<Record<string, number | null>>({});
  const [bands, setBands] = useState<Record<string, string>>({});
  const [enjoyedMost, setEnjoyedMost] = useState('');
  const [improvementNoticed, setImprovementNoticed] = useState('');
  const [recommend, setRecommend] = useState('');
  const [interestedInPrograms, setInterestedInPrograms] = useState(false);
  const [consentPromotional, setConsentPromotional] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submittedRef, setSubmittedRef] = useState<string | null>(null);
  const [step, setStep] = useState(0);

  const headingRef = useRef<HTMLHeadingElement>(null);
  const firstRender = useRef(true);

  const neverAttended = tenure === 'never';
  const totalSteps = neverAttended ? 3 : 4;

  const stepNames = neverAttended
    ? ['About you and your child', 'Your time with Schoolola', 'NAPLAN results and comments']
    : [
        'About you and your child',
        'Your time with Schoolola',
        'Your experience',
        'NAPLAN results and comments',
      ];

  useEffect(() => {
    if (step > totalSteps - 1) setStep(totalSteps - 1);
  }, [totalSteps, step]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    headingRef.current?.focus();
  }, [step]);

  const openCentres = useMemo(() => {
    const config = stateConfigs.find((s) => s.code === stateCode);
    return (config?.centers || []).filter((c) => c.isOpen);
  }, [stateConfigs, stateCode]);

  const programOptions = useMemo(
    () => [...BASE_PROGRAMS, ...(STATE_PROGRAMS[stateCode] || [])],
    [stateCode]
  );

  // Drop centre and programme selections that are no longer valid for the chosen state
  useEffect(() => {
    const allowed = programOptions.map((p) => p.value);
    setPrograms((prev) => prev.filter((p) => allowed.includes(p)));
  }, [programOptions]);

  useEffect(() => {
    if (centreSlug && !openCentres.some((c) => c.slug === centreSlug)) {
      setCentreSlug('');
    }
  }, [openCentres, centreSlug]);

  // Never attended fork: clear answers that are hidden
  useEffect(() => {
    if (neverAttended) {
      setPrograms([]);
      setRatings({});
      setRecommend('');
    } else {
      setInterestedInPrograms(false);
    }
  }, [neverAttended]);

  const blogLinks = useMemo(() => {
    const relevantTags = BLOG_TAGS[stateCode] || [];
    return blogPosts
      .filter((post) => {
        const states = post.location?.states || [];
        if (!states.includes(stateCode)) return false;
        const tags = post.tags || [];
        return tags.some((t) => relevantTags.includes(t));
      })
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 6);
  }, [stateCode]);

  const toggleProgram = (value: string) => {
    setPrograms((prev) =>
      prev.includes(value) ? prev.filter((p) => p !== value) : [...prev, value]
    );
  };

  const progress = useMemo(() => {
    const answered: boolean[] = [
      !!parentFirstName.trim(),
      !!parentLastName.trim(),
      !!parentEmail.trim(),
      !!childName.trim(),
      !!yearLevel,
      !!stateCode,
      !!tenure,
      ...BAND_QUESTIONS.map((q) => !!bands[q.key]),
      !!enjoyedMost.trim(),
      !!improvementNoticed.trim(),
    ];
    if (!neverAttended) {
      answered.push(programs.length > 0, !!recommend, ...RATING_QUESTIONS.map((q) => !!ratings[q.key]));
    } else {
      answered.push(interestedInPrograms);
    }
    const done = answered.filter(Boolean).length;
    return Math.round((done / answered.length) * 100);
  }, [
    parentFirstName, parentLastName, parentEmail, childName, yearLevel, stateCode, centreSlug,
    tenure, bands, enjoyedMost, improvementNoticed, neverAttended, programs, recommend, ratings,
    interestedInPrograms,
  ]);

  const stepComplete = (index: number): boolean => {
    const name = stepNames[index];
    if (name === 'About you and your child') {
      return !!(parentFirstName.trim() && parentLastName.trim() && parentEmail.trim() && childName.trim() && yearLevel && stateCode);
    }
    if (name === 'Your time with Schoolola') {
      return !!tenure && (neverAttended || programs.length > 0);
    }
    if (name === 'Your experience') {
      return RATING_QUESTIONS.every((q) => !!ratings[q.key]);
    }
    return (
      BAND_QUESTIONS.every((q) => !!bands[q.key]) &&
      !!enjoyedMost.trim() &&
      !!improvementNoticed.trim() &&
      (neverAttended || !!recommend)
    );
  };

  const validate = (): string[] => {
    const found: string[] = [];
    if (!parentFirstName.trim()) found.push('Enter your first name.');
    if (!parentLastName.trim()) found.push('Enter your last name.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(parentEmail.trim())) found.push('Enter a valid email address.');
    if (!childName.trim()) found.push("Enter your child's first name.");
    if (!yearLevel) found.push('Select a year level.');
    if (!stateCode) found.push('Select a state.');
    if (!tenure) found.push('Select how long your child has attended Schoolola.');
    BAND_QUESTIONS.forEach((q) => {
      if (!bands[q.key]) found.push(`Select a result for ${q.label.toLowerCase()}.`);
    });
    if (!enjoyedMost.trim()) found.push('Tell us what your child enjoyed most.');
    if (!improvementNoticed.trim()) found.push('Tell us what improvement you have noticed.');
    if (!neverAttended) {
      if (programs.length === 0) found.push('Select at least one programme.');
      RATING_QUESTIONS.forEach((q) => {
        if (!ratings[q.key]) found.push(`Rate: ${q.label}.`);
      });
      if (!recommend) found.push('Select whether you would recommend Schoolola.');
    }
    return found;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const found = validate();
    setErrors(found);
    if (found.length > 0) return;

    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        website_url: websiteUrl,
        elapsed_ms: Date.now() - loadedAt.current,
        parent_first_name: parentFirstName.trim(),
        parent_last_name: parentLastName.trim(),
        parent_email: parentEmail.trim(),
        child_name: childName.trim(),
        year_level: yearLevel,
        state_code: stateCode,
        centre_slug: centreSlug,
        tenure,
        enjoyed_most: enjoyedMost.slice(0, 500),
        improvement_noticed: improvementNoticed.slice(0, 500),
        interested_in_programs: neverAttended ? interestedInPrograms : false,
        consent_promotional: consentPromotional,
        consent_wording_text: consentPromotional ? CONSENT_WORDING : null,
      };

      BAND_QUESTIONS.forEach((q) => {
        payload[q.key] = bands[q.key] || null;
      });

      if (!neverAttended) {
        payload.programs = programs;
        payload.recommend = recommend;
        RATING_QUESTIONS.forEach((q) => {
          payload[q.key] = ratings[q.key];
        });
      }

      const { data, error } = await supabase.functions.invoke('naplan-feedback', { body: payload });

      if (error) {
        setErrors(['We could not submit your feedback. Please try again.']);
        return;
      }
      if (data && data.ok === false) {
        setErrors([data.error || 'We could not submit your feedback. Please try again.']);
        return;
      }
      setSubmittedRef(data?.submission_ref || 'received');
    } catch {
      setErrors(['We could not submit your feedback. Please try again.']);
    } finally {
      setSubmitting(false);
    }
  };

  const programLinks = [
    { label: 'NAPLAN preparation', to: '/programs/naplan-preparation' },
    ...(PROGRAM_LINKS[stateCode] || []),
  ];

  const currentName = stepNames[Math.min(step, stepNames.length - 1)];
  const isLastStep = step === totalSteps - 1;

  const renderStepBody = () => {
    if (currentName === 'About you and your child') {
      return (
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="min-w-0">
            <label className={labelClass} htmlFor="parent-first-name">Parent first name</label>
            <input
              id="parent-first-name"
              className={inputClass}
              value={parentFirstName}
              onChange={(e) => setParentFirstName(e.target.value)}
              required
            />
          </div>
          <div className="min-w-0">
            <label className={labelClass} htmlFor="parent-last-name">Parent last name</label>
            <input
              id="parent-last-name"
              className={inputClass}
              value={parentLastName}
              onChange={(e) => setParentLastName(e.target.value)}
              required
            />
          </div>
          <div className="min-w-0 sm:col-span-2">
            <label className={labelClass} htmlFor="parent-email">Parent email</label>
            <input
              id="parent-email"
              type="email"
              className={inputClass}
              value={parentEmail}
              onChange={(e) => setParentEmail(e.target.value)}
              required
            />
          </div>
          <div className="min-w-0">
            <label className={labelClass} htmlFor="child-name">Child first name</label>
            <input
              id="child-name"
              className={inputClass}
              value={childName}
              onChange={(e) => setChildName(e.target.value)}
              required
            />
          </div>
          <div className="min-w-0">
            <label className={labelClass} htmlFor="year-level">Year level</label>
            <select
              id="year-level"
              className={inputClass}
              value={yearLevel}
              onChange={(e) => setYearLevel(e.target.value)}
              required
            >
              <option value="">Select a year level</option>
              {YEAR_LEVELS.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="min-w-0">
            <label className={labelClass} htmlFor="state-code">State</label>
            <select
              id="state-code"
              className={inputClass}
              value={stateCode}
              onChange={(e) => setStateCode(e.target.value)}
              required
            >
              {STATE_OPTIONS.map((s) => (
                <option key={s.code} value={s.code}>{s.name}</option>
              ))}
            </select>
          </div>
          <div className="min-w-0">
            <label className={labelClass} htmlFor="centre-slug">Closest Schoolola centre (optional)</label>
            <select
              id="centre-slug"
              className={inputClass}
              value={centreSlug}
              onChange={(e) => setCentreSlug(e.target.value)}
              disabled={!stateCode}
            >
              <option value="">Select a centre</option>
              {openCentres.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Honeypot */}
          <div aria-hidden="true" className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden">
            <label htmlFor="website_url">Website</label>
            <input
              id="website_url"
              name="website_url"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
            />
          </div>
        </div>
      );
    }

    if (currentName === 'Your time with Schoolola') {
      return (
        <div>
          <div role="radiogroup" aria-label="How long has your child attended Schoolola?">
            <span className={legendClass}>
              How long has your child attended Schoolola? <span className="text-primary" aria-hidden="true">*</span>
            </span>
            <div className="grid gap-3 sm:grid-cols-2">
              {TENURE_OPTIONS.map((option) => (
                <OptionCard
                  key={option.value}
                  label={option.label}
                  selected={tenure === option.value}
                  onSelect={() => setTenure(option.value)}
                />
              ))}
            </div>
          </div>

          {!neverAttended && (
            <div className="mt-8" role="group" aria-label="Which programmes has your child taken?">
              <span className={legendClass}>
                Which programmes has your child taken? <span className="text-primary" aria-hidden="true">*</span>
              </span>
              <div className="grid gap-3 sm:grid-cols-2">
                {programOptions.map((option) => (
                  <OptionCard
                    key={option.value}
                    label={option.label}
                    multi
                    selected={programs.includes(option.value)}
                    onSelect={() => toggleProgram(option.value)}
                  />
                ))}
              </div>
            </div>
          )}

          {neverAttended && (
            <div className="mt-8">
              <OptionCard
                label="I would like to hear about Schoolola NAPLAN programs near me"
                multi
                selected={interestedInPrograms}
                onSelect={() => setInterestedInPrograms((v) => !v)}
              />
            </div>
          )}
        </div>
      );
    }

    if (currentName === 'Your experience') {
      return (
        <div className="space-y-3">
          {RATING_QUESTIONS.map((q) => {
            const active = !!ratings[q.key];
            return (
              <div
                key={q.key}
                role="radiogroup"
                aria-label={q.label}
                className={`flex flex-col gap-3 rounded-[14px] p-4 transition-colors md:flex-row md:items-center md:justify-between ${
                  active ? 'bg-accent/10' : 'bg-transparent'
                }`}
              >
                <span className="min-w-0 text-sm font-normal text-foreground md:pr-6">{q.label}</span>
                <div className="shrink-0">
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((value) => {
                      const selected = ratings[q.key] === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          aria-label={`${q.label}: ${value} of 5`}
                          onClick={() => setRatings((prev) => ({ ...prev, [q.key]: value }))}
                          className={`h-[46px] w-[46px] rounded-xl border-[1.5px] text-sm font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                            selected
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-input bg-background text-foreground hover:-translate-y-0.5 hover:border-primary'
                          }`}
                        >
                          {value}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-1.5 flex justify-between text-[11px] font-normal text-muted-foreground">
                    <span>Strongly disagree</span>
                    <span>Strongly agree</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div>
        <div className="grid gap-5 sm:grid-cols-2">
          {BAND_QUESTIONS.map((q) => (
            <div key={q.key} className="min-w-0">
              <label className={labelClass} htmlFor={q.key}>{q.label}</label>
              <select
                id={q.key}
                className={inputClass}
                value={bands[q.key] || ''}
                onChange={(e) => setBands((prev) => ({ ...prev, [q.key]: e.target.value }))}
              >
                <option value="">Select a result</option>
                {BAND_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          ))}
        </div>

        <div className="mt-6">
          <label className={labelClass} htmlFor="enjoyed-most">What did your child enjoy most?</label>
          <textarea
            id="enjoyed-most"
            className={textareaClass}
            rows={4}
            maxLength={500}
            value={enjoyedMost}
            onChange={(e) => setEnjoyedMost(e.target.value)}
            required
          />
          <p className="mt-1.5 text-xs text-muted-foreground font-normal">{enjoyedMost.length} / 500</p>
        </div>

        <div className="mt-6">
          <label className={labelClass} htmlFor="improvement-noticed">
            What improvement have you noticed?
          </label>
          <textarea
            id="improvement-noticed"
            className={textareaClass}
            rows={4}
            maxLength={500}
            value={improvementNoticed}
            onChange={(e) => setImprovementNoticed(e.target.value)}
            required
          />
          <p className="mt-1.5 text-xs text-muted-foreground font-normal">{improvementNoticed.length} / 500</p>
        </div>

        {!neverAttended && (
          <div className="mt-8" role="radiogroup" aria-label="Would you recommend Schoolola to other families?">
            <span className={legendClass}>Would you recommend Schoolola to other families?</span>
            <div className="grid gap-3 sm:grid-cols-2">
              {RECOMMEND_OPTIONS.map((option) => (
                <OptionCard
                  key={option.value}
                  label={option.label}
                  selected={recommend === option.value}
                  onSelect={() => setRecommend(option.value)}
                />
              ))}
            </div>
          </div>
        )}

        <div className="mt-8">
          <button
            type="button"
            role="checkbox"
            aria-checked={consentPromotional}
            onClick={() => setConsentPromotional((v) => !v)}
            className={`flex w-full items-start gap-3 rounded-[14px] border-[1.5px] p-4 text-left text-sm font-normal transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
              consentPromotional ? 'border-primary bg-primary/5' : 'border-input bg-background hover:border-primary'
            }`}
          >
            <span
              aria-hidden="true"
              className={`mt-0.5 flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-md border-[1.5px] ${
                consentPromotional ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-background'
              }`}
            >
              {consentPromotional && <Check className="h-3 w-3" />}
            </span>
            <span className="min-w-0 text-foreground">{CONSENT_WORDING}</span>
          </button>
        </div>

        <p className="mt-4 text-xs text-muted-foreground font-normal">
          Your feedback and any results you share are sent to your local Schoolola centre and to our head
          office. We handle your information in line with our{' '}
          <Link to="/privacy-policy" className="text-primary underline">Privacy Policy</Link>.
        </p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-cream">
      <Helmet>
        <title>NAPLAN parent feedback | Schoolola</title>
        <meta name="robots" content="noindex, follow" />
        <meta
          name="description"
          content="Share your feedback about your child's NAPLAN preparation with your local Schoolola centre."
        />
      </Helmet>

      <Header />

      <main className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute right-0 top-0 h-[520px] w-[520px] rounded-full opacity-40"
          style={{ background: 'radial-gradient(circle, hsl(var(--accent) / 0.25), transparent 70%)' }}
        />

        <div className="relative mx-auto grid max-w-[1120px] gap-10 px-4 py-12 lg:grid-cols-[340px_minmax(0,1fr)]">
          {/* Left rail */}
          <aside className="lg:sticky lg:top-16 lg:self-start">
            <h1 className="font-heading text-4xl font-bold tracking-[-0.015em] text-primary lg:text-[44px] lg:leading-[1.05]">
              NAPLAN parent feedback
            </h1>
            <p className="mt-4 text-muted-foreground font-normal">
              Tell us how your child found their NAPLAN preparation. Your answers go to your local Schoolola centre
              and help us improve our programmes.
            </p>

            <div className="my-7 h-px w-full bg-border" />

            {!submittedRef && (
              <>
                <div className="flex items-baseline justify-between">
                  <span className="text-xs font-semibold uppercase tracking-[0.12em] text-primary">Your progress</span>
                  <span className="text-xs font-normal text-muted-foreground">{progress}% complete</span>
                </div>
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-border">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${progress}%`,
                      transition: 'width .45s',
                      background: 'linear-gradient(90deg, hsl(var(--accent)), hsl(var(--primary)))',
                    }}
                  />
                </div>

                <nav className="mt-7 space-y-2" aria-label="Form steps">
                  {stepNames.map((name, index) => {
                    const active = index === step;
                    const complete = stepComplete(index);
                    return (
                      <button
                        key={name}
                        type="button"
                        onClick={() => setStep(index)}
                        aria-current={active ? 'step' : undefined}
                        className={`flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                          active ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <span
                          className={`flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full border-[1.5px] text-xs font-medium ${
                            active
                              ? 'border-primary bg-primary text-primary-foreground'
                              : complete
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-border bg-background text-muted-foreground'
                          }`}
                        >
                          {complete && !active ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : index + 1}
                        </span>
                        <span className="min-w-0">{name}</span>
                      </button>
                    );
                  })}
                </nav>
              </>
            )}
          </aside>

          {/* Right column */}
          <div className="min-w-0">
            {submittedRef ? (
              <section className="rounded-[24px] border border-border bg-card p-8 shadow-lg sm:p-11">
                <div
                  className="flex h-16 w-16 items-center justify-center rounded-full text-primary-foreground"
                  style={{ background: 'linear-gradient(135deg, hsl(var(--accent)), hsl(var(--primary)))' }}
                >
                  <Check className="h-8 w-8" aria-hidden="true" />
                </div>
                <h2 className={`${headingClass} mt-6 text-2xl`}>Thank you</h2>
                <p className="mt-3 text-sm text-muted-foreground font-normal">
                  We have sent a copy to your email address. Your reference is {submittedRef}.
                </p>
                <p className="mt-2 text-sm text-muted-foreground font-normal">
                  Your local centre will be in touch if there is anything they would like to follow up.
                </p>

                <div className="mt-8 grid gap-3 sm:grid-cols-2">
                  {programLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      aria-label={`Learn more about ${link.label}`}
                      className="flex items-center justify-between rounded-[14px] border border-border bg-background px-4 py-3 text-sm font-normal text-foreground transition-colors hover:border-primary"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <GraduationCap className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                        {link.label}
                      </span>
                      <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                    </Link>
                  ))}
                </div>
              </section>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                {errors.length > 0 && (
                  <div
                    className="mb-6 rounded-[18px] border border-destructive bg-destructive/10 p-4"
                    role="alert"
                    aria-live="polite"
                  >
                    <div className="flex items-start gap-2">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
                      <div className="min-w-0">
                        <p className="mb-1 text-sm font-medium text-destructive">Please check the following</p>
                        <ul className="list-disc space-y-0.5 pl-5 text-sm font-normal text-destructive">
                          {errors.map((err) => (
                            <li key={err}>{err}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                <section className="overflow-hidden rounded-[24px] border border-border bg-card shadow-lg">
                  <div key={step} className="animate-step-in p-6 sm:p-11">
                    <div aria-live="polite">
                      <p className="text-xs font-normal text-muted-foreground">Step {step + 1} of {totalSteps}</p>
                      <h2
                        ref={headingRef}
                        tabIndex={-1}
                        className={`${headingClass} mt-1 text-xl outline-none sm:text-2xl`}
                      >
                        {currentName}
                      </h2>
                    </div>
                    {currentName === 'Your experience' && (
                      <p className="mt-2 text-sm font-normal text-muted-foreground">
                        1 = Strongly disagree, 5 = Strongly agree
                      </p>
                    )}

                    <div className="mt-7">{renderStepBody()}</div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-4 border-t border-border px-6 py-5 sm:px-11">
                    <button
                      type="button"
                      onClick={() => setStep((s) => Math.max(0, s - 1))}
                      disabled={step === 0}
                      className="rounded-full px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
                    >
                      Back
                    </button>
                    <div className="flex items-center gap-4">
                      <span className="hidden text-xs font-normal text-muted-foreground sm:inline">
                        Step {step + 1} of {totalSteps}
                      </span>
                      {isLastStep ? (
                        <button
                          type="submit"
                          disabled={submitting}
                          className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
                        >
                          {submitting ? 'Submitting your feedback' : 'Submit feedback'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setStep((s) => Math.min(totalSteps - 1, s + 1))}
                          disabled={!stepComplete(step)}
                          className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
                        >
                          Continue
                        </button>
                      )}
                    </div>
                  </div>
                </section>
              </form>
            )}

            {/* Programmes for the chosen state */}
            {!submittedRef && (
              <section className="mt-10">
                <h2 className={`${headingClass} mb-4 text-lg`}>Programmes in your state</h2>
                <div className="grid gap-3 sm:grid-cols-2">
                  {programLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      aria-label={`Learn more about ${link.label}`}
                      className="flex items-center justify-between rounded-[14px] border border-border bg-card px-4 py-3 text-sm font-normal text-foreground transition-colors hover:border-primary"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <GraduationCap className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                        {link.label}
                      </span>
                      <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Featured guide */}
            <section className="mt-10">
              <h2 className={`${headingClass} mb-4 text-lg`}>Understanding your child's results</h2>
              <Link
                to="/blog/how-to-read-naplan-results"
                aria-label="Read the guide on how to read your child's NAPLAN results"
                className="block rounded-[14px] border border-primary/30 bg-primary/5 px-4 py-4 transition-colors hover:border-primary"
              >
                <span className="flex items-start gap-2">
                  <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-foreground">
                      How to read your child's NAPLAN results: a parent guide
                    </span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      What Exceeding, Strong, Developing and Needs additional support mean, domain by domain.
                    </span>
                  </span>
                </span>
              </Link>
            </section>

            {/* Blog links for the chosen state */}
            {blogLinks.length > 0 && (
              <section className="mt-10">
                <h2 className={`${headingClass} mb-4 text-lg`}>Reading for your state</h2>
                <div className="space-y-3">
                  {blogLinks.map((post) => (
                    <Link
                      key={post.slug}
                      to={getPostUrl(post.slug)}
                      aria-label={`Read the article ${post.title}`}
                      className="flex items-start gap-2 rounded-[14px] border border-border bg-card px-4 py-3 transition-colors hover:border-primary"
                    >
                      <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                      <span className="min-w-0 text-sm font-normal text-foreground">{post.title}</span>
                    </Link>
                  ))}
                </div>
                <Link
                  to="/blog"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary"
                >
                  See all articles <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </section>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NaplanFeedback;
